import { Injectable } from '@angular/core';
import jwtDecode from 'jwt-decode';
import { AuthTokenStorageService } from './auth-token-storage.service';

/** Raw JWT claims as emitted by CIPA participant authentication. */
interface RawTokenClaims {
    accessId?: string;
    participantPersonId?: string;
    tenantId?: string;
    electionId?: string;
    electionGuid?: string;
    establishmentId?: string;
    companySuffix?: string;
    participantName?: string;
    service?: string;
    sub?: string;
    nameid?: string;
    ['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']?: string;
}

/** Typed, parsed representation of the current authenticated user. */
export interface AuthUser {
    accessId: number;
    participantPersonId: number;
    electionId: number;
    electionGuid: string;
    companySuffix: string;
    isAnonymous: false;
    username: string;
    userId: string;
    isGlobal: boolean;
    hasAccessToAllAudiences: boolean;
    audiences: string[];
    currentTenant: string;
    isAcademicStaff: boolean;
}
export type CurrentUser = AuthUser;

@Injectable({
    providedIn: 'root',
})
export class AuthUserService {
    constructor(private readonly tokenStorage: AuthTokenStorageService) { }

    /** SMOCipa sempre opera em contexto autenticado de participante. */
    get isAnonymous(): boolean {
        return false;
    }

    /**
        * Returns the current user with typed claims.
     */
    getCurrentUser(): CurrentUser {
        const claims = this.decodeClaims();
        if (!claims) {
            return {
                accessId: 0,
                participantPersonId: 0,
                electionId: 0,
                electionGuid: '',
                companySuffix: '',
                isAnonymous: false,
                username: '',
                userId: '',
                isGlobal: false,
                hasAccessToAllAudiences: false,
                audiences: [],
                currentTenant: '',
                isAcademicStaff: false,
            };
        }

        return {
            isAnonymous: false,
            username: claims.participantName ?? '',
            accessId: claims.accessId ? Number(claims.accessId) : 0,
            participantPersonId: claims.participantPersonId ? Number(claims.participantPersonId) : 0,
            electionId: claims.electionId ? Number(claims.electionId) : 0,
            electionGuid: claims.electionGuid ?? '',
            companySuffix: claims.companySuffix ?? '',
            userId: claims.participantPersonId
                ?? claims.nameid
                ?? claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
                ?? claims.sub
                ?? '',
            isGlobal: false,
            hasAccessToAllAudiences: false,
            audiences: [],
            currentTenant: claims.tenantId ?? '',
            isAcademicStaff: false,
        };
    }

    /**
     * Returns a raw claim value by key, or null when the token is absent or the
     * key is not present.
     */
    getClaim<T = string>(claimKey: string): T | null {
        const claims = this.decodeClaims();
        if (!claims) {
            return null;
        }
        return ((claims as unknown as Record<string, unknown>)[claimKey] as T) ?? null;
    }

    private decodeClaims(): RawTokenClaims | null {
        const token = this.tokenStorage.getAccessToken();
        if (!token) {
            return null;
        }

        try {
            return jwtDecode<RawTokenClaims>(token);
        } catch {
            return null;
        }
    }
}
